/**
 * useFilteredMenuItems.js
 *
 * Returns only the menu items the current user is allowed to see.
 * Each menu item / group can declare `permissions` or `roles` arrays.
 * Items without any guard are visible to all authenticated users.
 *
 * Usage:
 *   const items = useFilteredMenuItems(rawMenuItems);
 *
 * Menu item shape:
 *   {
 *     id: 'users',
 *     title: 'Users',
 *     type: 'item',
 *     url: '/users',
 *     // optional guards:
 *     permissions: ['users.read'],          // show if user has ANY of these
 *     roles: ['super_admin', 'system_admin'], // show if user has ANY of these
 *   }
 */
import { useMemo } from 'react';
import { usePermission } from 'hooks/usePermission';

export const useFilteredMenuItems = (navItems) => {
    const { canAny, hasRole, isAdmin } = usePermission();

    const isVisible = (item) => {
        if (isAdmin) return true;
        if (!item.permissions?.length && !item.roles?.length) return true;

        const permPass = !item.permissions?.length || canAny(item.permissions);
        const rolePass = !item.roles?.length || hasRole(item.roles);

        return permPass && rolePass;
    };

    const filterItems = (items) =>
        items
            .filter(isVisible) // ← check item's own roles/permissions first
            .map((item) => {
                // For 'collapse' and 'group' types that have children,
                // recursively filter the children and drop the parent if all children
                // are hidden (even if the parent's own guard passed).
                if (item.children?.length) {
                    const filtered = filterItems(item.children);
                    if (!filtered.length) return null; // collapse with no visible children → hide it
                    return { ...item, children: filtered };
                }
                return item;
            })
            .filter(Boolean);

    return useMemo(
        () => ({
            ...navItems,
            items: navItems.items
                .map((group) => {
                    if (group.type === 'group') {
                        const filtered = filterItems(group.children ?? []);
                        if (!filtered.length) return null;
                        return { ...group, children: filtered };
                    }
                    return isVisible(group) ? group : null;
                })
                .filter(Boolean)
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [navItems, isAdmin]
    );
};
